import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant.js";

/**
 * @typedef {Object} OnboardingStudent
 * @property {number} id
 * @property {string} name
 * @property {string} username
 * @property {string|null} email
 * @property {string} age
 * @property {string} status
 * @property {boolean} has_transferred_progress
 */

/**
 * @typedef {Object} OnboardingStudentsResponse
 * @property {OnboardingStudent[]} students
 */

/**
 * Custom hook to fetch the parent's onboarding students list.
 * Uses the parent token from parentLocalData in localStorage
 * (set by switchToParent) instead of fetchWithAuth.
 */
export const useGetOnboardingStudents = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** @type {[OnboardingStudentsResponse|null, Function]} */
  const [data, setData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchOnboardingStudents = async (dummy_required = false) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      const parentLocalData = JSON.parse(
        localStorage.getItem("parentLocalData"),
      );
      const parentToken = parentLocalData?.token;

      if (!parentToken) {
        throw new Error(ERROR_MESSAGES.FORBIDDEN);
      }

      const response = await fetch(`${apiUrl}/parent/onboarding/students`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${parentToken}`,
        },
      });

      if (!response.ok) {
        let errorMsg = ERROR_MESSAGES?.UNKNOWN || "Something went wrong";
        try {
          const errorData = await response.json();
          if (errorData?.errors) {
            const errorKey = Object.keys(errorData.errors)[0];
            errorMsg = errorData.errors[errorKey][0];
          } else {
            errorMsg = errorData?.message || errorMsg;
          }
        } catch {
          if (response.status === 500) {
            errorMsg =
              ERROR_MESSAGES?.INTERNAL_SERVER || "Internal server error";
          }
        }
        throw new Error(errorMsg);
      }

      /** @type {OnboardingStudentsResponse} */
      const responseData = await response.json();

      // Filter out dummy students (age is null) — they are auto-created
      // for dashboard exploration and should not appear in any consumer.
      if (!dummy_required) {
        if (responseData?.students) {
          responseData.students = responseData.students.filter(
            (student) => student.age != null,
          );
        }
      }

      setData(responseData);
    } catch (err) {
      setError(
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong",
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchOnboardingStudents,
    loading,
    error,
    data,
    setError,
  };
};
